import { Formik, useFormikContext } from 'formik';
import type { OptionType } from '../../../../util/types';
import type { TimeAssessmentFilterEnum } from '@whizz/api-model';
import type { DateTime } from 'luxon';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Selector from '../../../../components/selector/Selector';
import Textarea from '../../../../components/input/Textarea';
import { QUERIES_IDS } from '../../../../util/vars';
import Button from '../../../../components/button/Button';
import * as Yup from 'yup';
import { useI18n } from '@whizz/react-i18n';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { model, repository } from '@whizz/api-client';
import { useDateRangeSelector } from '../../../../hooks/filters/useDateRangeSelector';
import { useImpactReportTemplateSelector } from '../../../../hooks/useImpactReportTemplateSelector';
import { ImpactReportEditor } from './ImpactReportEditor';
import { ImpactReportGradingSummary } from './ImpactReportGradingSummary';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { getDatesParams } from '../../../../helper/getDatesParams';
import NoData from '../../../../global/sections/NoData';
import Alert from '../../../../components/alert/Alert';
import debounce from 'lodash/debounce';

interface GenerateImpactReportFormValues {
  template: string | null;
  prompt: string;
  startDate: TimeAssessmentFilterEnum | DateTime,
  endDate: DateTime | undefined,
  locale: string;
  to: OptionType<number>[];
  cc: OptionType<string>[];
  bcc: OptionType<string>[];
}

interface SendImpactReportValues {
  startDate: TimeAssessmentFilterEnum | DateTime,
  endDate: DateTime | undefined,
  content: string;
}

type ConfigurationFormikValues = Pick<GenerateImpactReportFormValues, 'prompt' | 'to' | 'cc' | 'bcc'>;
type ConfigurationAutoSaveValues = Pick<GenerateImpactReportFormValues, 'prompt' | 'to' | 'cc' | 'bcc'>;

const AutoSaveConfigurationFields = ({ onSave }: { onSave: (values: ConfigurationAutoSaveValues) => void; }) => {
  const { values, isSubmitting } = useFormikContext<ConfigurationFormikValues>();
  const previousSnapshotRef = useRef<string>(JSON.stringify({
    to: values.to,
    cc: values.cc,
    bcc: values.bcc,
    prompt: values.prompt ?? '',
  }));

  useEffect(() => {
    const nextValues = {
      to: values.to,
      cc: values.cc,
      bcc: values.bcc,
      prompt: values.prompt ?? '',
    };
    const serialized = JSON.stringify(nextValues);

    if (serialized === previousSnapshotRef.current || isSubmitting) {
      return;
    }

    const debouncedSave = debounce(() => {
      previousSnapshotRef.current = serialized;
      onSave(nextValues);
    }, 600);

    debouncedSave();

    return () => debouncedSave.cancel();
  }, [ values.to, values.cc, values.bcc, values.prompt, onSave, isSubmitting ]);

  return null;
};

const VALIDATION_SCHEMA = () => Yup.object().shape({
  to: Yup.array().of(Yup.mixed<OptionType<number>>()),
  cc: Yup.array().of(Yup.object().shape({
    label: Yup.string(),
    value: Yup.string().email(),
  })),
  bcc: Yup.array().of(Yup.object().shape({
    label: Yup.string(),
    value: Yup.string().email(),
  })),
  prompt: Yup.string().optional(),
});

const ImpactReportConfigurationForm = () => {
  const { translate: t } = useI18n();
  const history = useHistory();
  const location = useLocation();
  const { schoolId, jobId } = useParams<{ schoolId: string; jobId?: string }>();

  const impactReportsBasePath = useMemo(() => {
    const normalizedPath = location.pathname.endsWith('/') && location.pathname !== '/' ? location.pathname.slice(0, -1) : location.pathname;

    if (!jobId) {
      return normalizedPath;
    }

    const suffix = `/${jobId}`;
    return normalizedPath.endsWith(suffix) ? normalizedPath.slice(0, -suffix.length) : normalizedPath;
  }, [ location.pathname, jobId ]);

  const queryClient = useQueryClient();

  const academicYearStartDateQuery = useQuery<DateTime>(
    [ QUERIES_IDS.ACADEMIC_YEAR_START_DATE, schoolId ],
    () => model.School.academicYearStartDate(Number(schoolId)),
    {
      enabled: null != schoolId,
    },
  );

  const implementationPlanQuery = useQuery({
    queryKey: [ QUERIES_IDS.ADMIN.GET_SCHOOL_IMPLEMENTATION_PLAN, schoolId ],
    queryFn: () => repository.SchoolImplementationPlanRepository.get(Number(schoolId)),
    enabled: null != schoolId,
  });

  const isImplementationPlanMissing = !implementationPlanQuery.data || !implementationPlanQuery.data.goals.length;
  const isImpactReportFormDisabled = isImplementationPlanMissing;

  const [ sendStatus, setSendStatus ] = useState<'idle' | 'saving' | 'sending' | 'saved' | 'sent'>('idle');
  const [ dateRangeSelector, dateValue, customDateValue ] = useDateRangeSelector({ maxDate: new Date(), label: 'Generate using data from...', useCurrentAcademicYearOrLastAssessment: true });
  const [ templateSelector, selectedTemplate ] = useImpactReportTemplateSelector({ disabled: isImpactReportFormDisabled });

  const selectedDateInterval = dateValue?.value ?? 'last_week';

  const {
    startDateParam,
    endDateParam,
  } = getDatesParams(selectedDateInterval, customDateValue, academicYearStartDateQuery.data ?? null, null);

  const getSchoolTeachersQuery = useQuery({
    queryKey: [ QUERIES_IDS.ADMIN.GET_SCHOOL_TEACHERS, { id: schoolId } ],
    queryFn: () => repository.AdminRepository.getSchoolTeachers(Number(schoolId)),
  });

  const getSchoolImpactReportConfigurationQuery = useQuery({
    queryKey: [ QUERIES_IDS.ADMIN.GET_SCHOOL_IMPACT_REPORT_CONFIGURATION, { id: schoolId } ],
    queryFn: () => repository.SchoolImpactReportRepository.getConfiguration(Number(schoolId)),
    enabled: null != schoolId,
  });

  const saveSchoolImpactReportConfigurationMutation = useMutation({
    mutationFn: (data: any) => repository.SchoolImpactReportRepository.saveConfiguration(Number(schoolId), {
      to: data.to.map((option: OptionType<number> & { firstName: string }) => option.value),
      cc: data.cc.map((option: OptionType<string>) => option.value),
      bcc: data.bcc.map((option: OptionType<string>) => option.value),
      prompt: data.prompt,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries([ QUERIES_IDS.ADMIN.GET_SCHOOL_IMPACT_REPORT_CONFIGURATION, { id: schoolId } ]);
    },
  });

  const sendImpactReportMutation = useMutation({
    mutationFn: ({ data }: { data: SendImpactReportValues }) => {
      if (null == jobId) {
        return Promise.reject(new Error('No impact report job available.'));
      }

      return repository.SchoolImpactReportRepository.saveAndSend(Number(schoolId), jobId, {
        startDate: startDateParam as TimeAssessmentFilterEnum | DateTime,
        endDate: endDateParam as DateTime,
        body: data.content,
      });
    },
  });

  const saveImpactReportMutation = useMutation({
    mutationFn: ({ data }: { data: SendImpactReportValues }) => {
      if (null == jobId) {
        return Promise.reject(new Error('No impact report job available.'));
      }

      return repository.SchoolImpactReportRepository.save(Number(schoolId), jobId, {
        startDate: startDateParam as TimeAssessmentFilterEnum | DateTime,
        endDate: endDateParam as DateTime,
        body: data.content,
      });
    },
  });

  const generateImpactReportMutation = useMutation({
    mutationFn: ({ data }: { data: Omit<GenerateImpactReportFormValues, 'to' | 'cc' | 'bcc'> }) => repository.SchoolImpactReportRepository.generate(Number(schoolId), {
      template: data.template!,
      prompt: data.prompt,
      startDate: data.startDate,
      endDate: data.endDate,
      locale: data.locale,
    }),
    onSuccess: (data) => {
      const nextPath = `${impactReportsBasePath}/${data.jobId}`;
      if (location.pathname !== nextPath) {
        history.replace(nextPath);
      }
      queryClient.invalidateQueries([ QUERIES_IDS.ADMIN.IMPACT_REPORT_JOB, data.jobId ]);
    },
  });

  const impactReportJobQuery = useQuery({
    queryKey: [ QUERIES_IDS.ADMIN.IMPACT_REPORT_JOB, jobId ],
    queryFn: () => repository.SchoolImpactReportRepository.getStatus(Number(schoolId), jobId!),
    enabled: null != jobId,
    refetchInterval: (data) => (data?.status === 'complete' || data?.status === 'error' ? false : 2000),
  });

  const state = getSchoolImpactReportConfigurationQuery.isLoading ? 'loading' : (getSchoolImpactReportConfigurationQuery.data ? 'loaded' : 'error');
  const errorGeneratingReport = (generateImpactReportMutation.isError || impactReportJobQuery.isError || (impactReportJobQuery.isFetched && impactReportJobQuery.data?.status === 'error'));
  const isGeneratingReport = !errorGeneratingReport && (generateImpactReportMutation.isLoading || impactReportJobQuery.isLoading || (impactReportJobQuery.isFetched && impactReportJobQuery.data?.status !== 'complete'));
  const leadersConfig = getSchoolImpactReportConfigurationQuery.data ?? null;
  const { mutate: saveConfiguration } = saveSchoolImpactReportConfigurationMutation;
  const handleAutoSave = useCallback((configValues: ConfigurationAutoSaveValues) => {
    saveConfiguration(configValues);
  }, [ saveConfiguration ]);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (saveImpactReportMutation.isLoading) {
      setSendStatus('saving');
    } else if (saveImpactReportMutation.isSuccess) {
      setSendStatus('saved');
      timer = setTimeout(() => {
        saveImpactReportMutation.reset();
      }, 3000);
    } else if (sendImpactReportMutation.isLoading) {
      setSendStatus('sending');
    } else if (sendImpactReportMutation.isSuccess) {
      setSendStatus('sent');
      timer = setTimeout(() => {
        sendImpactReportMutation.reset();
      }, 3000);
    } else {
      setSendStatus('idle');
    }
    return () => clearTimeout(timer);
  }, [ saveImpactReportMutation.isLoading, saveImpactReportMutation.isSuccess, saveImpactReportMutation.isError, sendImpactReportMutation.isLoading, sendImpactReportMutation.isSuccess, sendImpactReportMutation.isError ]);

  if (state === 'loading') {
    return <NoData noDataMessage={t('pages.ADMIN.IMPACT_REPORTS.LOADING')} />;
  }

  return (
    <div className={'flex flex-col gap-5'}>
      { isImplementationPlanMissing ? <Alert show type={'warning'}>
        {t('pages.ADMIN.IMPACT_REPORTS.DISABLED')}
      </Alert> : null }
      <Formik<ConfigurationFormikValues>
        validationSchema={VALIDATION_SCHEMA()}
        initialValues={{
          to: leadersConfig?.to?.map((teacher) => ({
            label: `${teacher.firstName} ${teacher.lastName}`,
            value: teacher.id,
            firstName: teacher.firstName,
          })) ?? [],
          cc: leadersConfig?.cc?.map((item) => ({ label: item, value: item })) ?? [],
          bcc: leadersConfig?.bcc?.map((item) => ({ label: item, value: item })) ?? [],
          prompt: leadersConfig?.prompt || '',
        }}
        onSubmit={(values) => {
          if (isImpactReportFormDisabled || !selectedTemplate) {
            return;
          }

          generateImpactReportMutation.mutate({
            data: {
              ...values,
              template: selectedTemplate,
              startDate: startDateParam as TimeAssessmentFilterEnum | DateTime,
              endDate: endDateParam as DateTime | undefined,
              locale: 'en-gb',
            },
          });
        }}
      >
        {({ values, errors, setFieldValue, handleSubmit, isValid }) => {
          return (
            <form onSubmit={handleSubmit} className={'space-y-4'}>
              <fieldset disabled={isImpactReportFormDisabled} className={`space-y-4 ${isImpactReportFormDisabled ? 'opacity-50' : ''}`}>
                <AutoSaveConfigurationFields onSave={handleAutoSave} />
                <div className={'flex gap-5'}>
                  <div className={'flex flex-col gap-5 w-full'}>
                    <Selector
                      id={'to'}
                      label={t('pages.ADMIN.FORM.LABELS.TO')}
                      placeholder={t('pages.ADMIN.FORM.PLACEHOLDERS.TO')}
                      options={(getSchoolTeachersQuery.data?.data ?? []).map((teacher) => ({
                        label: `${teacher.firstName} ${teacher.lastName}`,
                        value: teacher.id,
                        firstName: teacher.firstName,
                      }))}
                      onChange={(options: (OptionType<number> & { firstName: string })[]) => {
                        setFieldValue('to', options);
                      }}
                      value={values.to}
                      error={errors.to}
                      isDisabled={isImpactReportFormDisabled || getSchoolTeachersQuery.isFetching}
                      isMulti
                    />

                    <Selector
                      id={'cc'}
                      label={t('pages.ADMIN.FORM.LABELS.CC')}
                      placeholder={t('pages.ADMIN.FORM.PLACEHOLDERS.CC')}
                      onChange={(options: OptionType<string>[]) => setFieldValue('cc', options)}
                      value={values.cc}
                      error={0 < (errors.cc?.length ?? 0) ? t('pages.ADMIN.FORM.ERRORS.INVALID_EMAILS') : null}
                      customComponents={{
                        DropdownIndicator: () => null,
                        IndicatorSeparator: () => null,
                      }}
                      isCreatable
                      isMulti
                      isDisabled={isImpactReportFormDisabled}
                    />

                    <Selector
                      id={'bcc'}
                      label={t('pages.ADMIN.FORM.LABELS.BCC')}
                      placeholder={t('pages.ADMIN.FORM.PLACEHOLDERS.BCC')}
                      onChange={(options: OptionType<string>[]) => setFieldValue('bcc', options)}
                      value={values.bcc}
                      error={0 < (errors.bcc?.length ?? 0) ? t('pages.ADMIN.FORM.ERRORS.INVALID_EMAILS') : null}
                      customComponents={{
                        DropdownIndicator: () => null,
                        IndicatorSeparator: () => null,
                      }}
                      isCreatable
                      isMulti
                      isDisabled={isImpactReportFormDisabled}
                    />
                  </div>
                  <div className={'flex flex-col gap-5 w-full'}>

                    {templateSelector}

                    <Textarea
                      id={'prompt'}
                      label={t('pages.ADMIN.IMPACT_REPORTS.FORM.LABELS.PROMPT')}
                      placeholder={t('pages.ADMIN.IMPACT_REPORTS.FORM.PLACEHOLDERS.PROMPT')}
                      onChange={(event) => {
                        setFieldValue('prompt', event.target.value);
                      }}
                      value={values.prompt ?? ''}
                      error={errors.prompt}
                      disabled={isImpactReportFormDisabled}
                    />
                    <div className={`w-full ${isImpactReportFormDisabled ? 'pointer-events-none opacity-50' : ''}`}>
                      {dateRangeSelector}
                    </div>
                  </div>
                </div>
                <div className={'flex justify-center'}>
                  <Button
                    type={'submit'}
                    color={'positive'}
                    className={'w-1/3'}
                    disabled={isImpactReportFormDisabled || isGeneratingReport || !isValid || !selectedTemplate}
                  >
                    {t(`pages.ADMIN.IMPACT_REPORTS.BUTTONS.${isGeneratingReport ? 'GENERATING' : 'GENERATE'}`)}
                  </Button>
                </div>
              </fieldset>
            </form>
          );
        }}
      </Formik>
      {errorGeneratingReport ? 'Error' : null}
      <ImpactReportGradingSummary grading={impactReportJobQuery.data?.grading} />
      {isGeneratingReport
        ? <NoData noDataMessage={t('pages.ADMIN.IMPACT_REPORTS.GENERATING')} />
        : impactReportJobQuery.data?.report ? <ImpactReportEditor content={impactReportJobQuery.data.report} save={saveImpactReportMutation.mutate} send={sendImpactReportMutation.mutate} status={sendStatus} /> : null
      }
    </div>
  );
};

export { ImpactReportConfigurationForm };
